import { useState, useEffect, useRef } from 'react';

import { Asset } from '@/modules/case/types';

import { AssetViewerConfig } from '../types';

export const useAssetViewer = (
  assets: Asset[],
  initialIndex: number,
  isOpen: boolean,
  config: AssetViewerConfig
) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [speakerVideoUrl, setSpeakerVideoUrl] = useState<string | null>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Swipe states
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isVerticalSwipe, setIsVerticalSwipe] = useState(false);
  const [swipeDelta, setSwipeDelta] = useState(0);
  const [isImageZoomed, setIsImageZoomed] = useState(false);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  useEffect(() => {
    setSpeakerVideoUrl(null);
    setSwipeOffset(0);
    setIsTransitioning(false);
    setIsVerticalSwipe(false);
    if (thumbnailRefs.current[currentIndex]) {
      thumbnailRefs.current[currentIndex]?.scrollIntoView({
        behavior: config.transitions.thumbnailScroll,
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [currentIndex, config.transitions.thumbnailScroll]);

  useEffect(() => {
    const updateSwipeDelta = () => {
      setSwipeDelta(window.innerWidth * 0.25);
    };

    updateSwipeDelta();
    window.addEventListener('resize', updateSwipeDelta);

    return () => window.removeEventListener('resize', updateSwipeDelta);
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Force a micro-scroll to hide the mobile browser address bar
      setTimeout(() => {
        window.scrollTo(0, 1);
      }, 100);
    }
  }, [isOpen]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : assets.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < assets.length - 1 ? prev + 1 : 0));
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
  };

  return {
    currentIndex,
    speakerVideoUrl,
    setSpeakerVideoUrl,
    thumbnailRefs,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    swipeOffset,
    setSwipeOffset,
    isTransitioning,
    setIsTransitioning,
    isVerticalSwipe,
    setIsVerticalSwipe,
    swipeDelta,
    isImageZoomed,
    setIsImageZoomed,
  };
};
